import { useMemo, useState } from "react";
import { hotComments, memes, scenes, topics, site } from "@/mocks/chiagua";

function gotoSection(id: string) {
  document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
}

export default function Hero() {
  const [kw, setKw] = useState("");
  const [focused, setFocused] = useState(false);

  // 综合索引：把神评、梗图、话题做成可搜索卡片
  const index = useMemo(() => {
    const fromComments = hotComments.map((c) => ({
      type: "神评",
      title: c.quote,
      meta: c.tag,
      to: "hot",
    }));
    const fromMemes = memes.map((m) => ({ type: "梗图", title: m.title, meta: m.category, to: "memes" }));
    const fromScenes = scenes.map((s) => ({ type: "名场面", title: s.title, meta: s.category, to: "scenes" }));
    const fromTopics = topics.map((t) => ({ type: "话题", title: t.name, meta: t.tag, to: "topics" }));
    return [...fromComments, ...fromMemes, ...fromScenes, ...fromTopics];
  }, []);

  const results = useMemo(() => {
    const q = kw.trim();
    if (!q) return [];
    return index
      .filter((it) => (it.title + it.meta).includes(q))
      .slice(0, 8);
  }, [kw, index]);

  return (
    <section id="top" className="relative overflow-hidden bg-background-100 pt-16">
      {/* 底色装饰 */}
      <div className="pointer-events-none absolute inset-0 dot-grid opacity-60" aria-hidden="true"></div>
      <div
        className="pointer-events-none absolute -right-24 -top-24 h-72 w-72 rounded-full bg-primary-200/60 blur-3xl"
        aria-hidden="true"
      ></div>
      <div
        className="pointer-events-none absolute -left-16 bottom-0 h-64 w-64 rounded-full bg-accent-200/60 blur-3xl"
        aria-hidden="true"
      ></div>

      <div className="relative mx-auto grid max-w-6xl items-center gap-10 px-4 py-12 md:px-6 md:py-16 lg:grid-cols-[1.05fr_0.95fr]">
        <div className="text-center lg:text-left">
          <p className="inline-flex items-center gap-2 rounded-full bg-background-50 px-4 py-1.5 text-sm font-medium text-primary-700">
            <span className="relative flex h-2 w-2">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-primary-500 opacity-70"></span>
              <span className="relative inline-flex h-2 w-2 rounded-full bg-primary-500"></span>
            </span>
            今日评论区高能更新中
          </p>

          <h1 className="mt-5 font-heading text-4xl leading-tight text-foreground-950 md:text-5xl lg:text-[3.4rem]">
            围观每一处
            <span className="text-primary-500">评论区名场面</span>
            <br />
            把快乐一次吃个够
          </h1>

          <p className="mx-auto mt-5 max-w-xl text-base leading-relaxed text-foreground-600 md:text-lg lg:mx-0">
            网罗抖音神评论合集与沙雕热评，高清吃瓜梗图、搞笑表情包一站式放齐。
            跟着 <strong className="font-bold text-foreground-950">{site.brand}</strong>，边吃瓜边攒素材，笑到根本停不下来。
          </p>

          {/* 搜索框 */}
          <div className="relative mx-auto mt-7 max-w-xl lg:mx-0">
            <form
              role="search"
              onSubmit={(e) => {
                e.preventDefault();
                if (results.length > 0) gotoSection(results[0].to);
              }}
              className="flex items-center gap-2 rounded-full bg-background-50 p-2 shadow-[0_8px_24px_-14px_oklch(var(--foreground-950)/0.3)]"
            >
              <i className="ri-search-line ml-3 text-lg text-foreground-500" aria-hidden="true"></i>
              <input
                type="text"
                value={kw}
                onChange={(e) => {
                  setKw(e.target.value);
                  setFocused(true);
                }}
                onFocus={() => setFocused(true)}
                onBlur={() => setTimeout(() => setFocused(false), 150)}
                placeholder="搜神评 / 名场面 / 梗图，如：打工人"
                className="w-full bg-transparent text-sm text-foreground-900 outline-none placeholder:text-foreground-400"
              />
              <button
                type="submit"
                className="whitespace-nowrap rounded-full bg-primary-500 px-5 py-2.5 text-sm font-bold text-background-50 transition-colors hover:bg-primary-600"
              >
                开搜
              </button>
            </form>

            {focused && results.length > 0 && (
              <div className="absolute inset-x-0 top-full z-20 mt-2 overflow-hidden rounded-2xl bg-background-50 p-2 text-left shadow-[0_16px_40px_-12px_oklch(var(--foreground-950)/0.3)]">
                {results.map((r, i) => (
                  <button
                    key={i}
                    type="button"
                    onMouseDown={() => {
                      gotoSection(r.to);
                      setFocused(false);
                    }}
                    className="flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-left hover:bg-background-200"
                  >
                    <span className="shrink-0 rounded-md bg-secondary-100 px-2 py-0.5 text-xs font-bold text-secondary-700">
                      {r.type}
                    </span>
                    <span className="truncate text-sm text-foreground-800">{r.title}</span>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* 快捷标签 */}
          <div className="mt-6 flex flex-wrap justify-center gap-2 lg:justify-start">
            {["沙雕热评", "萌宠神评", "打工人日常", "表情包大户", "深夜emo"].map((t) => (
              <a
                key={t}
                href="#memes"
                className="rounded-full bg-background-200/70 px-4 py-1.5 text-sm text-foreground-700 transition-colors hover:bg-accent-200 hover:text-accent-900"
              >
                #{t}
              </a>
            ))}
          </div>
        </div>

        {/* 右侧贴纸组合 */}
        <div className="relative mx-auto w-full max-w-sm lg:max-w-none">
          <div className="relative h-[320px] md:h-[380px]">
            <div className="absolute left-0 top-0 w-56 md:w-64">
              <img
                src="https://readdy.ai/api/search-image?query=a%20big%20cheerful%20cartoon%20popcorn%20bucket%20character%20with%20star%20eyes%20laughing%20amid%20floating%20hearts%2C%20viral%20fun%20energy%2C%20flat%20vector%20sticker%20illustration%2C%20warm%20cream%20and%20soft%20peach%20background%2C%20coral%20red%20and%20leaf%20green%20accents%2C%20clean%20minimal%2C%20no%20text&width=420&height=420&seq=hero-a&orientation=squarish"
                alt="爆米花贴纸，代表吃瓜围观"
                className="w-full rounded-3xl object-cover shadow-[0_18px_40px_-18px_oklch(var(--foreground-950)/0.4)]"
              />
            </div>
            <div className="absolute bottom-0 right-0 w-56 md:w-64">
              <img
                src="https://readdy.ai/api/search-image?query=a%20slice%20of%20watermelon%20mascot%20wearing%20cool%20sunglasses%20and%20smiling%20surrounded%20by%20sparkles%2C%20gossip%20and%20fun%20vibe%2C%20flat%20vector%20sticker%20illustration%2C%20warm%20cream%20and%20soft%20peach%20background%2C%20coral%20red%20and%20leaf%20green%20accents%2C%20clean%20minimal%2C%20no%20text&width=420&height=420&seq=hero-b&orientation=squarish"
                alt="戴墨镜的西瓜贴纸，吃瓜人本瓜"
                className="w-full rounded-3xl object-cover shadow-[0_18px_40px_-18px_oklch(var(--foreground-950)/0.4)]"
              />
            </div>
            <div className="absolute right-6 top-8 hidden w-36 animate-floaty md:block">
              <img
                src="https://readdy.ai/api/search-image?query=a%20phone%20screen%20showing%20a%20huge%20comment%20bubble%20with%20thumbs%20up%20and%20confetti%20burst%2C%20social%20buzz%20energy%2C%20flat%20vector%20sticker%20illustration%2C%20warm%20cream%20and%20soft%20peach%20background%2C%20coral%20red%20and%20leaf%20green%20accents%2C%20clean%20minimal%2C%20no%20text&width=420&height=420&seq=hero-c&orientation=squarish"
                alt="手机评论气泡贴纸"
                className="w-full rounded-2xl object-cover"
              />
            </div>
            <div className="absolute bottom-6 left-2 flex h-14 w-14 animate-floaty items-center justify-center rounded-full bg-secondary-400 text-2xl text-background-50 [animation-delay:1.2s]">
              <i className="ri-message-3-fill" aria-hidden="true"></i>
            </div>
          </div>

          <div className="relative z-10 -mt-6 mx-auto max-w-md rounded-2xl bg-background-50 p-4 shadow-[0_10px_30px_-16px_oklch(var(--foreground-950)/0.3)] lg:-mt-2">
            <div className="flex items-start gap-3">
              <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-accent-100 text-lg text-accent-700">
                <i className="ri-quote-text" aria-hidden="true"></i>
              </div>
              <p className="text-sm leading-relaxed text-foreground-800">
                "本来想来看视频，结果在评论区笑到手机没电，今日份快乐全靠神评续命。"
              </p>
            </div>
            <div className="mt-2 flex items-center justify-between pl-12 text-xs text-foreground-500">
              <span>来自评论区野生段子手</span>
              <button
                type="button"
                className="flex items-center gap-1 rounded-full bg-primary-100 px-3 py-1 font-medium text-primary-700"
              >
                <i className="ri-fire-fill" aria-hidden="true"></i> 12.8万热度
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* 滚动热评条 */}
      <div className="relative border-t border-background-200 bg-background-50/70 backdrop-blur-sm">
        <div className="mx-auto flex max-w-6xl items-center gap-4 px-4 py-3 md:px-6">
          <span className="inline-flex shrink-0 items-center gap-1.5 rounded-full bg-secondary-500 px-3 py-1 text-xs font-bold text-background-50">
            <i className="ri-fire-line" aria-hidden="true"></i> 滚动热评
          </span>
          <div className="relative flex-1 overflow-hidden">
            <div className="flex w-max animate-ticker gap-10 whitespace-nowrap">
              {[...ticker, ...ticker].map((t, i) => (
                <span key={i} className="text-sm text-foreground-700">
                  {t}
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

const ticker = [
  "评论区人均段子手，随手一翻全是高手",
  "本来想来看视频，结果在评论区笑到打鸣",
  "一条热评的杀伤力，能顶三条正片",
  "名场面警告：前方神评论高能",
  "吃瓜一时爽，一直吃瓜一直爽",
  "评论区才是我的快乐老家",
];